import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faArrowLeft, faShoppingBag, faRedo } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../../styles/theme';
import Footer from '../../Footer/Footer';

const FailureContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
`;

const ContentContainer = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
`;

const FailureCard = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.light};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  margin: 2rem 0;
`;

const FailureIcon = styled.div`
  font-size: 4rem;
  color: #f44336;
  margin-bottom: 1.5rem;
`;

const FailureTitle = styled.h1`
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
  font-size: 2rem;
`;

const FailureMessage = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ErrorDetails = styled.div`
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  text-align: left;
`;

const ErrorDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  min-width: 180px;
  justify-content: center;
  
  &.primary {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
    
    &:hover {
      background: ${props => props.theme.colors.primaryDark};
      box-shadow: ${props => props.theme.shadows.medium};
    }
  }
  
  &.secondary {
    background: ${props => props.theme.colors.white};
    color: ${props => props.theme.colors.primary};
    border: 2px solid ${props => props.theme.colors.primary};
    
    &:hover {
      background: ${props => props.theme.colors.primary};
      color: ${props => props.theme.colors.white};
    }
  }
  
  &.danger {
    background: #f44336;
    color: ${props => props.theme.colors.white};
    
    &:hover {
      background: #d32f2f;
      box-shadow: ${props => props.theme.shadows.medium};
    }
  }
`;

const HelpText = styled.div`
  background: ${props => props.theme.colors.primaryLight};
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  text-align: left;
`;

const HelpTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const HelpList = styled.ul`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
  padding-left: 1.5rem;
`;

const PaymentFailure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const orderNumber = searchParams.get('order');
  const errorCode = searchParams.get('error_code');
  const errorMessage = searchParams.get('error_message');
  const paymentIntentId = searchParams.get('payment_intent');

  useEffect(() => {
    // Auto-redirect after 15 seconds
    const timer = setTimeout(() => {
      navigate('/duchess-collection/checkout');
    }, 15000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleRetryPayment = () => {
    navigate('/duchess-collection/checkout');
  };

  const handleBackToShop = () => {
    navigate('/duchess-collection');
  };

  return (
    <FailureContainer>
      <ContentContainer>
        <FailureCard>
          <FailureIcon>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </FailureIcon>
          
          <FailureTitle>Payment Failed</FailureTitle>
          
          <FailureMessage>
            We're sorry, but your payment could not be processed. This could be due to insufficient funds, incorrect card details, or a temporary issue with your bank.
          </FailureMessage>

          {(orderNumber || errorCode || paymentIntentId) && (
            <ErrorDetails>
              {orderNumber && (
                <ErrorDetailRow>
                  <span>Order Number:</span>
                  <span>#{orderNumber}</span>
                </ErrorDetailRow>
              )}
              {paymentIntentId && (
                <ErrorDetailRow>
                  <span>Payment ID:</span>
                  <span>{paymentIntentId}</span>
                </ErrorDetailRow>
              )}
              {errorCode && (
                <ErrorDetailRow>
                  <span>Error Code:</span>
                  <span>{errorCode}</span>
                </ErrorDetailRow>
              )}
              {errorMessage && (
                <ErrorDetailRow>
                  <span>Error Message:</span>
                  <span>{errorMessage}</span>
                </ErrorDetailRow>
              )}
            </ErrorDetails>
          )}

          <HelpText>
            <HelpTitle>What you can do:</HelpTitle>
            <HelpList>
              <li>Check that your card details are correct</li>
              <li>Ensure you have sufficient funds available</li>
              <li>Try using a different payment method</li>
              <li>Contact your bank if the issue persists</li>
              <li>Try again in a few minutes</li>
            </HelpList>
          </HelpText>

          <ActionButtons>
            <Button 
              className="primary" 
              onClick={handleRetryPayment}
            >
              <FontAwesomeIcon icon={faRedo} />
              Try Again
            </Button>
            
            <Button 
              className="secondary" 
              onClick={handleBackToShop}
            >
              <FontAwesomeIcon icon={faShoppingBag} />
              Continue Shopping
            </Button>
          </ActionButtons>

          <p style={{ marginTop: '2rem', fontSize: '0.9rem', color: '#666' }}>
            You will be automatically redirected to try again in 15 seconds...
          </p>
        </FailureCard>
      </ContentContainer>
      
      <Footer />
    </FailureContainer>
  );
};

export default PaymentFailure;